/**
 * Component show summary (total) information about user's order
 *
 * @attr {String} [order-main-text] - Cart Summary title text
 * @attr {String} [product-text] - Product text label
 * @attr {String} [products-text] - Products text label
 * @attr {String} [total-discount-text] - Total discount title text
 * @attr {String} [total-price-without-delivery-text] - Total price title text (without delivery)
 * @attr {String} [total-price-with-delivery-text] - Total price title text (with delivery)
 * @attr {String} [delivery-text] - Delivery information text
 * @attr {String} [home-delivery-only-text] - Home delivery only text label
 * @attr {String} [delivery-tooltip-text] - The text shown as a static tooltip under the delivery info line when
 * @attr {String} [delivery-free-text] - The text shown as a price when the delivery costs 0
 * @attr {String} [min-tax-text] - Low VAT amount text
 * @attr {String} [max-tax-text] - High VAT amount text
 * @attr {Boolean} [hide-msg] - Hide all info msg from component (fot checkout or order details pages)
 *
 *
 * Parameters - comes from redux store:
 *
 * @param {Number} totalItems - The number of total items in the cart
 * @param {Object} totalPrice - Object with sum of the price of products and delivery costs.
 * @param {Object} [subTotal] - Object with sum of the price of products without discounts and without delivery
 * @param {Object} [totalDiscounts] - Object with sum of all discounts applied
 * @param {String} [totalPriceWithoutDelivery] - Object with sum of the price of products without the delivery costs
 * @param {Object} [deliveryCost] - Object with cost of delivery
 * @param {String} [highTaxes] - Sum of all high taxes
 * @param {String} [lowTaxes] - Sum of all low taxes
 * @param {Array}  [appliedPromotions] - Applied promotions for current order
 * @param {String} [totalPoints] - The number of total loyalty points of the product in the cart
 * @param {Boolean} [isRegularCart] - Check if cart contains only regular product
 * @param {Boolean} [isDigitalCart] - Check if cart contains only digital product
 * @param {Boolean} [isLoyaltyCart] - Check if cart contains only loyalty product
 * @param {Boolean} [isMixedCart] - Check if cart contains regular and/or digital/loyalty product
 *
 * HTML Structure Example
 *
 * <e2-cart-summary
 *      order-main-text="Cart Summary"
 *      product-text="product"
 *      products-text="products"
 *      total-discount-text="Total discount"
 *      total-price-without-delivery-text="Total (without delivery)"
 *      total-price-with-delivery-text="Total (with delivery)"
 *      delivery-text="Delivery information"
 *      home-delivery-only-text="Home delivery only text"
 *      delivery-tooltip-text="At the next step you can choose to pick up at the store"
 *      delivery-free-text="Free"
 *      min-tax-text="Low VAT amount"
 *      max-tax-text="High VAT amount"
 *      hide-msg
 * ></e2-cart-summary>
 */

import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';

import { SimpleLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { selectors } from '@elab/core/store/cart';
import {
    DEFULT_PRICE_OBJ,
    getFormattedDiscount,
    getFormattedPrice,
    priceFormatter,
    priceFormatterForceNegative,
} from '@elab/core/utils/PriceUtils';
import { evalCartType } from '@elab/core/utils/cart/CartUtils';

const CART_SUMMARY_CLASS = 'cart-summary';

export class CartSummary extends SimpleLitElement {
    static get properties() {
        return {
            // From redux
            totalItems: { type: Number },
            totalPrice: { type: Object },
            subTotal: { type: Object },
            totalDiscounts: { type: Object },
            totalPriceWithoutDelivery: { type: String },
            deliveryCost: { type: Object },
            highTaxes: { type: String },
            lowTaxes: { type: String },
            appliedPromotions: { type: Array },
            totalPoints: { type: Number },
            isDigitalCart: { type: Boolean },
            isRegularCart: { type: Boolean },
            isMixedCart: { type: Boolean },
            isLoyaltyCart: { type: Boolean },
            cartType: { type: String },
            totalDiscountWithoutPayWithPoints: { type: Object },
            PWPPromotion: { type: Object },

            // From attributes
            orederMainText: { type: String, attribute: 'order-main-text' },
            productText: { type: String, attribute: 'product-text' },
            productsText: { type: String, attribute: 'products-text' },
            totalDiscountText: {
                type: String,
                attribute: 'total-discount-text',
            },
            totalPriceWithoutDeliveryText: {
                type: String,
                attribute: 'total-price-without-delivery-text',
            },
            totalPriceWithDeliveryText: {
                type: String,
                attribute: 'total-price-with-delivery-text',
            },
            deliveryText: { type: String, attribute: 'delivery-text' },
            pickupText: { type: String, attribute: 'pickup-text' },
            homeDeliveryOnlyText: {
                type: String,
                attribute: 'home-delivery-only-text',
            },
            deliveryTooltipText: {
                type: String,
                attribute: 'delivery-tooltip-text',
            },
            deliveryFreeText: { type: String, attribute: 'delivery-free-text' },
            lowTaxText: { type: String, attribute: 'min-tax-text' },
            highTaxText: { type: String, attribute: 'max-tax-text' },
            pointsLabel: { type: String, attribute: 'points-label' },
            orderCode: { type: String, attribute: 'order-code' },
            hideMsg: { type: Boolean, attribute: 'hide-msg' },
            cssModifier: { type: String, attribute: 'css-modifier' },
            lineSeparator: { type: Boolean, attribute: 'line-separator' },
            promotionRow: { type: Boolean, attribute: 'promotion-row' },
            bottomPromotions: { type: Boolean, attribute: 'bottom-promotions' },
        };
    }

    afterConnect() {
        if (this.orderCode) {
            this.dispatchEvent(new Event('load-order'));
        }
    }

    isFreeDelivery() {
        return this.deliveryCost.value === 0;
    }

    deliveryRow() {
        let label;
        if (this.homeDeliveryOnly) {
            label = this.homeDeliveryOnlyText;
        } else if (this.deliveryMode.code === 'pickup') {
            label = this.pickupText;
        } else {
            label = this.deliveryText;
        }
        const value = this.isFreeDelivery()
            ? this.deliveryFreeText
            : getFormattedPrice(this.deliveryCost);
        return html`
            ${cartSummaryBlock(label, value, '--small')}
            ${!this.isFreeDelivery() && !this.hideMsg
                ? html`
                      <section class="delivery-msg">
                          <div class="delivery-msg__inner">
                              ${this.deliveryTooltipText}
                          </div>
                      </section>
                  `
                : ''}
        `;
    }

    getTitleBlock() {
        const orderTitleText = `${this.orederMainText} (${this.totalItems} ${
            this.totalItems === 1 ? this.productText : this.productsText
        })`;
        const subtotal = this.isLoyaltyCart ? '' : getFormattedPrice(this.subTotal);
        return cartSummaryBlock(
            orderTitleText,
            subtotal,
            '--big',
            false,
            this.totalPoints,
            this.pointsLabel,
        );
    }

    getAppliedPromotionsBlock() {
        return (this.isRegularCart || this.isMixedCart) &&
            this.promotionRow &&
            !this.bottomPromotions
            ? cartPromotionsList(this.appliedPromotions)
            : '';
    }

    getBottomPromotions() {
        return this.bottomPromotions && this.appliedPromotions && this.appliedPromotions.length
            ? cartPromotionsList(this.appliedPromotions, !this.PWPPromotion, '--bottom', true)
            : '';
    }

    getTotalDiscounts() {
        const totDiscounts = this.isLoyaltyCart
            ? ''
            : this.totalDiscountWithoutPayWithPoints || this.totalDiscounts;

        return cartSummaryBlock(
            this.totalDiscountText,
            getFormattedDiscount(totDiscounts),
            '--small',
            !this.PWPPromotion && !(this.bottomPromotions && this.appliedPromotions),
        );
    }

    getPayWithPointsPromotions() {
        const { amount, message } = this.PWPPromotion;
        return amount && message
            ? cartSummaryBlock(message, getFormattedDiscount(amount), '--small', true)
            : '';
    }

    getTotalPriceWithoutDelivery() {
        return !this.isLoyaltyCart
            ? cartSummaryBlock(
                  this.totalPriceWithoutDeliveryText,
                  getFormattedPrice(this.totalPriceWithoutDelivery),
                  this.cssModifier,
                  this.lineSeparator,
              )
            : '';
    }

    getDeliveryRow() {
        return this.isLoyaltyCart || this.isDigitalCart ? '' : this.deliveryRow();
    }

    render() {
        return !this.isEmpty
            ? html`
                  <div class="${CART_SUMMARY_CLASS}">
                      <div class="${CART_SUMMARY_CLASS}__recap">
                          ${this.getTitleBlock()} ${this.getAppliedPromotionsBlock()}
                          ${this.getTotalDiscounts()} ${this.getBottomPromotions()}
                          ${this.PWPPromotion ? this.getPayWithPointsPromotions() : ''}
                          ${this.getTotalPriceWithoutDelivery()} ${this.getDeliveryRow()}
                      </div>
                      ${cartSummaryTotalBlock(
                          this.isLoyaltyCart,
                          this.totalPrice,
                          this.totalPoints,
                          this.totalPriceWithDeliveryText,
                          this.pointsLabel,
                          this.highTaxes,
                          this.lowTaxes,
                          this.highTaxText,
                          this.lowTaxText,
                      )}
                  </div>
              `
            : html``;
    }
}

function cartPromotionsList(promotions, separator, cssModifier, noNegative) {
    const list = promotions.map(({ amount = {}, message, promotionType }, index) => {
        if (promotionType === 'PWP') return;

        const promoValue = noNegative
            ? priceFormatter(amount.value, amount.currencyISOCode)
            : amount.formattedValue ||
              priceFormatterForceNegative(-amount.value, amount.currencyISOCode);

        const isLastElement = index === promotions.length - 1;
        const needSeparator = separator && isLastElement;
        return cartSummaryBlock(message, promoValue, '--promotion', needSeparator);
    });
    const elementCSSClass = `${CART_SUMMARY_CLASS}__promotions`;
    const elementCSSModifier = (cssModifier && `${elementCSSClass}${cssModifier}`) || '';

    return html`
        <div class="${elementCSSClass} ${elementCSSModifier}">${list}</div>
    `;
}

function getCartSummaryClasses(isValue, isSecondValue) {
    const map = {
        [`${CART_SUMMARY_CLASS}__text`]: true,
        [`${CART_SUMMARY_CLASS}__text--value`]: isValue,
        [`${CART_SUMMARY_CLASS}__text--mobile-space`]: isSecondValue,
    };
    return classMap(map);
}

function cartSummaryBlock(
    label = '',
    value = '',
    cssModifier = [],
    lineSeparator,
    secondValue = '',
    pointsLabel = '',
) {
    cssModifier = Array.isArray(cssModifier) ? cssModifier : [cssModifier];
    const className = `${CART_SUMMARY_CLASS}__value`;

    return html`
        <h5 class="${className} ${cssModifier.map(mod => className + mod).join(' ')}">
            <span class="${getCartSummaryClasses()}">${label}</span>
            ${value
                ? html`
                      <span class="${getCartSummaryClasses(true)}">
                          ${value}
                      </span>
                  `
                : ''}
            ${secondValue
                ? html`
                      <span class="${getCartSummaryClasses(false, true)}">
                          ${secondValue}<span class="cart-summary__points">${pointsLabel}</span>
                      </span>
                  `
                : ''}
        </h5>
        ${lineSeparator
            ? html`
                  <hr class="${CART_SUMMARY_CLASS}__line" />
              `
            : ''}
    `;
}

function cartSummaryTotalBlock(
    isLoyaltyCart,
    totalPrice = [],
    totalPoints,
    totalPriceWithDeliveryText = '',
    pointsLabel = '',
    highTaxes,
    lowTaxes,
    highTaxText = '',
    lowTaxText = '',
    cssModifier = [],
) {
    cssModifier = Array.isArray(cssModifier) ? cssModifier : [cssModifier];
    const className = `${CART_SUMMARY_CLASS}__total-price`;
    const highTax = highTaxes || '€ 0,00';
    const lowTax = lowTaxes || '€ 0,00';
    return html`
        <div class="${CART_SUMMARY_CLASS}__totals">
            <div>
                ${cartSummaryBlock(totalPriceWithDeliveryText, '', '--biggest')}
                ${!isLoyaltyCart
                    ? html`
                          <p class="${CART_SUMMARY_CLASS}__taxes">
                              ${highTaxText} ${highTax}
                          </p>
                          <p class="${CART_SUMMARY_CLASS}__taxes">
                              ${lowTaxText} ${lowTax}
                          </p>
                      `
                    : ''}
            </div>

            <div class="${CART_SUMMARY_CLASS}__total-flex">
                ${!isLoyaltyCart
                    ? html`
                          <h4
                              class="${className} ${cssModifier
                                  .map(mod => className + mod)
                                  .join(' ')}"
                          >
                              ${getFormattedPrice(totalPrice)}
                          </h4>
                      `
                    : ''}
                ${isLoyaltyCart || totalPoints > 0
                    ? html`
                          <h4
                              class="${className} ${cssModifier
                                  .map(mod => className + mod)
                                  .join(' ')}"
                          >
                              ${totalPoints}<span class="cart-summary__points">${pointsLabel}</span>
                          </h4>
                      `
                    : ''}
            </div>
        </div>
    `;
}

const mapStateToProps = state => {
    const cart = selectors.getData(state);
    const totalPrice = cart.totalPrice || DEFULT_PRICE_OBJ;

    return {
        totalItems: cart.totalUnitCount || 0,
        totalPrice,
        subTotal: cart.totalPriceWithoutPromotions || totalPrice,
        totalDiscounts: cart.totalDiscounts || DEFULT_PRICE_OBJ,
        totalPriceWithoutDelivery: cart.totalPriceWithoutDelivery || totalPrice,
        deliveryCost: cart.deliveryCost || DEFULT_PRICE_OBJ,
        deliveryMode: selectors.getDeliveryMode(state),
        highTaxes: selectors.getTaxAmount(state, 'H').formattedValue || '',
        lowTaxes: selectors.getTaxAmount(state, 'L').formattedValue || '',
        appliedPromotions: cart.appliedPromotions || [],
        isEmpty: selectors.isEmpty(state),
        totalPoints: cart.redeemLoyaltyPoints,
        ...evalCartType(cart.cartType, totalPrice),
        homeDeliveryOnly: selectors.getHomeDeliveryOnly(state),
        PWPPromotion: selectors.getAppliedPWPPromotion(state),
        totalDiscountWithoutPayWithPoints: cart.totalDiscountWithoutPayWithPoints || '',
    };
};

export default () =>
    customElements.define('e2-cart-summary', connect(mapStateToProps)(CartSummary));
